// src/pages/ModuleDetailPage.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, BookOpen, Clock, Users, CheckCircle } from 'lucide-react';
import { modulesByLevel } from '../data/modules';
import { useApp } from '../context/AppContext';
import ConversationModuleDetail from '../components/modules/ConversationModuleDetail';
import { setReadingModule, clearReadingModule } from '../services/companionModuleContext';
// Removed Button import - using DDD button classes directly
// Removed Card import - using glassmorphism styling directly

const ModuleDetailPage = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { moduleProgress, setModuleProgress } = useApp();
  const [view, setView] = useState('overview'); // Start with overview first

  // Find the module across all levels
  const allModules = Object.values(modulesByLevel).flat();
  const module = allModules.find(m => m.id === moduleId);

  // While this page is open, the menu link into Marlow tells her which module
  // the teller is reading.
  const readingId = module?.id;
  const readingTitle = module?.title;
  useEffect(() => {
    if (!readingId) return undefined;
    setReadingModule({ id: readingId, title: readingTitle });
    return () => clearReadingModule(readingId);
  }, [readingId, readingTitle]);

  if (!module) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="min-h-screen pt-24 px-6 pb-6"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-bold mb-4">Module Not Found</h1>
          <p className="text-pearl/80 mb-8">The requested module could not be found.</p>
          <button onClick={() => navigate('/modules')} className="btn-secondary">
            Back to Modules
          </button>
        </div>
      </motion.div>
    );
  }

  const isCompleted = moduleProgress?.[moduleId]?.completed || false;

  const handleStartModule = () => {
    // Mark module as started
    setModuleProgress(prev => ({
      ...prev,
      [moduleId]: {
        ...prev?.[moduleId],
        started: true,
        startedAt: new Date().toISOString()
      }
    }));
    
    // Start the learning experience directly on this page
    setView('learning');
  };

  const handleCompleteModule = () => {
    // Mark module as completed
    setModuleProgress(prev => ({
      ...prev,
      [moduleId]: {
        ...prev?.[moduleId],
        completed: true,
        completedAt: new Date().toISOString(),
        progress: 100
      }
    }));

    // Find the next module
    const currentIndex = allModules.findIndex(m => m.id === moduleId);
    const nextModule = allModules[currentIndex + 1];

    // Navigate to the next module if one exists, otherwise go back to modules list
    if (nextModule) {
      navigate(`/modules/${nextModule.id}`);
      // Reset view to overview for the next module
      setView('overview');
    } else {
      // No more modules, go back to modules list
      navigate('/modules');
    }
  };

  // Show learning interface if requested
  if (view === 'learning') {
    return (
      <ConversationModuleDetail
        module={module}
        onComplete={handleCompleteModule}
        onBack={() => setView('overview')}
      />
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="min-h-screen pt-32 pb-12 px-4 sm:px-6"
    >
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/modules')}
          className="mb-6 flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Modules
        </button>

        {/* Hero Header with Image */}
        <div className="relative mb-8 rounded-2xl overflow-hidden shadow-2xl">
          {/* Background Image */}
          <div className="relative h-80">
            <img
              src={module.heroImage || module.image || `/module-${module.order?.toString().padStart(2, '0')}.png`}
              alt={module.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                const fallback = document.createElement('div');
                fallback.className = 'w-full h-full bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900';
                e.target.parentNode.appendChild(fallback);
              }}
            />
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/20" />

            {/* Content Overlay */}
            <div className="absolute inset-0 flex flex-col justify-end p-8">
              <div className="flex items-center gap-3 mb-3">
                <span className="px-3 py-1 bg-indigo-500/30 backdrop-blur-sm border border-indigo-400/50 rounded-full text-xs font-bold text-indigo-200 uppercase tracking-wide">
                  {module.difficulty} Module
                </span>
                <span className="flex items-center gap-2 text-slate-300 text-sm">
                  <Clock className="w-4 h-4" />
                  {module.duration || '45 minutes'}
                </span>
              </div>
              <h1 className="text-5xl font-bold bg-gradient-to-r from-white via-indigo-100 to-purple-200 bg-clip-text text-transparent mb-2">
                {module.title}
              </h1>
              {module.subtitle && (
                <p className="text-xl text-slate-300 font-medium">
                  {module.subtitle}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-700/50 p-8 rounded-xl shadow-xl">
              <h2 className="text-2xl font-bold mb-4 text-white flex items-center gap-3">
                <BookOpen className="w-6 h-6 text-indigo-400" />
                About This Module
              </h2>
              <p className="text-slate-300 text-lg leading-relaxed">
                {module.description}
              </p>
            </div>

            {/* Learning Objectives */}
            <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 backdrop-blur-sm border border-indigo-500/30 p-8 rounded-xl shadow-xl">
              <h3 className="text-2xl font-bold mb-6 text-white flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-indigo-400" />
                What You'll Learn
              </h3>
              <ul className="space-y-4">
                {(module.content?.learningObjectives || module.objectives || [
                  "Understand the storytelling ideas in this module",
                  "Apply them to a story from your own life",
                  "Practise with the module's exercises",
                  "Tell the story to someone and notice what lands"
                ]).map((objective, index) => (
                  <li key={index} className="flex items-start gap-4 p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                    <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-indigo-500/50">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-slate-200 text-base leading-relaxed pt-1">{objective}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Module Content Preview */}
            <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-700/50 p-8 rounded-xl shadow-xl">
              <h3 className="text-2xl font-bold mb-6 text-white flex items-center gap-3">
                <BookOpen className="w-6 h-6 text-purple-400" />
                Module Sections
              </h3>
              <div className="space-y-3">
                {(module.content?.sections || [
                  { title: "Introduction", duration: "10 min" },
                  { title: "Core Ideas", duration: "15 min" },
                  { title: "Worked Examples", duration: "20 min" },
                  { title: "Practice", duration: "25 min" }
                ]).map((section, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-slate-800/50 hover:bg-slate-800 rounded-lg transition-all group border border-slate-700/50 hover:border-purple-500/50">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500/20 to-pink-500/20 group-hover:from-purple-500/30 group-hover:to-pink-500/30 rounded-lg flex items-center justify-center border border-purple-500/30">
                      <span className="text-purple-300 font-bold">{index + 1}</span>
                    </div>
                    <span className="text-white font-medium flex-1">{section.title}</span>
                    <span className="text-sm text-slate-400 flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {section.duration || '15 min'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Module Info Card */}
            <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-700/50 p-6 rounded-xl shadow-xl sticky top-32">
              <h4 className="text-lg font-bold text-white mb-6">Module Details</h4>
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 border border-cyan-500/30">
                    <Clock className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Duration</p>
                    <p className="text-white font-semibold">{module.duration || '45 minutes'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg flex items-center justify-center flex-shrink-0 border border-purple-500/30">
                    <Users className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Difficulty</p>
                    <p className="text-white font-semibold capitalize">{module.difficulty}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 bg-gradient-to-br ${isCompleted ? 'from-green-500/20 to-emerald-500/20 border-green-500/30' : 'from-amber-500/20 to-orange-500/20 border-amber-500/30'} rounded-lg flex items-center justify-center flex-shrink-0 border`}>
                    <CheckCircle className={`w-5 h-5 ${isCompleted ? 'text-green-400' : 'text-amber-400'}`} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Status</p>
                    <p className={`font-semibold ${isCompleted ? 'text-green-400' : 'text-amber-400'}`}>
                      {isCompleted ? 'Completed ✓' : 'Not completed yet'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="my-6 border-t border-slate-700/50" />

              {/* CTA Button */}
              <div>
                {!isCompleted ? (
                  <button
                    onClick={handleStartModule}
                    className="w-full py-3 px-4 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-bold rounded-lg shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 transition-all hover:scale-105 flex items-center justify-center gap-2"
                  >
                    <BookOpen className="w-5 h-5" />
                    Start Learning
                  </button>
                ) : (
                  <button
                    onClick={handleStartModule}
                    className="w-full py-3 px-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold rounded-lg shadow-lg shadow-green-500/30 hover:shadow-green-500/50 transition-all hover:scale-105 flex items-center justify-center gap-2"
                  >
                    <BookOpen className="w-5 h-5" />
                    Review Module
                  </button>
                )}
              </div>
            </div>

            {/* Prerequisites */}
            {module.prerequisites && module.prerequisites.length > 0 && (
              <div className="bg-amber-500/10 backdrop-blur-sm border border-amber-500/30 p-6 rounded-xl shadow-xl">
                <h4 className="text-lg font-bold mb-4 text-white flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-amber-400" />
                  Prerequisites
                </h4>
                <div className="space-y-3">
                  {module.prerequisites.map((prereq, index) => {
                    const prereqModule = allModules.find(m => m.id === prereq);
                    const isPrereqCompleted = moduleProgress?.[prereq]?.completed;

                    return (
                      <div key={index} className={`flex items-center gap-3 p-3 rounded-lg border ${isPrereqCompleted ? 'bg-green-500/10 border-green-500/30' : 'bg-slate-800/30 border-slate-600/30'}`}>
                        <CheckCircle
                          className={`w-5 h-5 ${isPrereqCompleted ? 'text-green-400' : 'text-slate-500'}`}
                        />
                        <span className={`text-sm font-medium ${isPrereqCompleted ? 'text-green-300' : 'text-slate-400'}`}>
                          {prereqModule?.title || prereq}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ModuleDetailPage;